import { z } from 'zod';

import {
  TASK_CATEGORIES,
  TASK_CATEGORY_AVAILABILITY,
  TASK_PRODUCT_TYPES,
  WORD_COUNT_RANGES,
  BRAND_VOICES,
  WRITER_EXPERIENCE_LEVELS,
  LANGUAGES,
} from '@/lib/constants';

// MM/DD/YYYY with OPTIONAL " HH:MM" 24-hour time. Server converts to ISO.
const DEADLINE_MMDDYYYY =
  /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}( ([01]\d|2[0-3]):([0-5]\d))?$/;

// Single guideline file entry (URL + display name) stored in tasks.attachments.
const guidelineFileSchema = z.object({
  url: z.string().url('Must be a valid URL'),
  name: z.string().trim().min(1, 'Filename is required').max(200),
});
export type GuidelineFile = z.infer<typeof guidelineFileSchema>;

const guidelineFilesArraySchema = z
  .array(guidelineFileSchema)
  .max(3, 'You can upload up to 3 guideline files')
  .optional();

// Inner object schema, exported so the task-template validations can build a
// `.partial()` on top of it without duplicating field rules.
export const createTaskObjectSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  category: z
    .enum(TASK_CATEGORIES, { error: 'Category is required' })
    .refine((v) => TASK_CATEGORY_AVAILABILITY[v], {
      message: 'This category is coming soon and cannot be selected yet',
    }),
  // Relaxed from a closed enum to a length-bounded string so workspaces can
  // pick a custom Content Type stored in workspace_content_types. The server
  // verifies the value is either a built-in (TASK_CONTENT_TYPES) or exists in
  // the caller's workspace before accepting the row.
  content_type: z
    .string()
    .trim()
    .min(1, 'Content type is required')
    .max(80, 'Content type must be 80 characters or fewer'),
  product_category: z.enum(TASK_PRODUCT_TYPES, {
    error: 'Product type is required',
  }),
  product_type_other: z.string().trim().max(80).optional().or(z.literal('')),
  word_count_range: z.enum(WORD_COUNT_RANGES, {
    error: 'Word count range is required',
  }),
  research_required: z.boolean(),
  brand_voice: z.enum(BRAND_VOICES, {
    error: 'Brand voice preference is required',
  }),
  guidelines_url: z.string().url('Must be a valid URL').optional().or(z.literal('')),
  guidelines_files: guidelineFilesArraySchema,
  description: z.string().min(1, 'Instructions are required'),
  required_experience_level: z.enum(WRITER_EXPERIENCE_LEVELS, {
    error: 'Experience level is required',
  }),
  language: z.enum(LANGUAGES, { error: 'Language is required' }),
  amount: z
    .number()
    .min(1, 'Fee must be at least $1')
    .max(1000, 'Fee must be at most $1,000'),
  deadline: z
    .string()
    .regex(DEADLINE_MMDDYYYY, 'Use MM/DD/YYYY or MM/DD/YYYY HH:MM')
    .optional()
    .or(z.literal('')),
  assigned_writer_id: z
    .string()
    .uuid('Invalid writer ID')
    .optional()
    .or(z.literal('').transform(() => undefined)),
});

export const createTaskSchema = createTaskObjectSchema.superRefine((val, ctx) => {
  if (val.product_category === 'Other') {
    const other = val.product_type_other?.trim();
    if (!other) {
      ctx.addIssue({
        code: 'custom',
        path: ['product_type_other'],
        message: 'Please specify the product type',
      });
    }
  }
});

export type CreateTaskInput = z.infer<typeof createTaskSchema>;

// Partial for updates — `.superRefine` is dropped by `.partial()` on ZodObject,
// so we rebuild the shape without the cross-field check for edits. Edit UI
// still enforces the "Other" requirement at the form level.
export const updateTaskSchema = z
  .object({
    title: z.string().min(1, 'Title is required').optional(),
    category: z
      .enum(TASK_CATEGORIES)
      .refine((v) => TASK_CATEGORY_AVAILABILITY[v], {
        message: 'This category is coming soon and cannot be selected yet',
      })
      .optional(),
    content_type: z.string().trim().min(1).max(80).optional(),
    product_category: z.enum(TASK_PRODUCT_TYPES).optional(),
    product_type_other: z.string().trim().max(80).optional().or(z.literal('')),
    word_count_range: z.enum(WORD_COUNT_RANGES).optional(),
    research_required: z.boolean().optional(),
    brand_voice: z.enum(BRAND_VOICES).optional(),
    guidelines_url: z.string().url().optional().or(z.literal('')),
    guidelines_files: guidelineFilesArraySchema,
    description: z.string().min(1).optional(),
    required_experience_level: z.enum(WRITER_EXPERIENCE_LEVELS).optional(),
    language: z.enum(LANGUAGES).optional(),
    amount: z.number().min(1).max(1000).optional(),
    deadline: z
      .string()
      .regex(DEADLINE_MMDDYYYY, 'Use MM/DD/YYYY or MM/DD/YYYY HH:MM')
      .optional()
      .or(z.literal('')),
    assigned_writer_id: z
      .string()
      .uuid()
      .optional()
      .or(z.literal('').transform(() => undefined)),
  });
export type UpdateTaskInput = z.infer<typeof updateTaskSchema>;

export const declineTaskSchema = z.object({
  reason: z.string().min(1, 'Reason is required'),
});

export type DeclineTaskInput = z.infer<typeof declineTaskSchema>;
